/**
 * Calendário de Caixa do ciclo inter-6DU (Task #49) — companion do card 6DU.
 *
 * Mostra o MOVIMENTO de caixa por data de vencimento REAL (Zeus dt_pagamento)
 * dentro do ciclo [6DU atual, próximo 6DU). A coluna do 6DU reconcilia com o
 * card 6DU; o "movimento acumulado" (running sum, começa em 0) revela o vale:
 * mergulho no 6DU (saída das compras) e a recuperação conforme as vendas
 * vencem ao longo do ciclo. NÃO é saldo absoluto — saldo real vem na Fase 2
 * (#44). Datas vêm do calendário CCEE oficial (nunca heurística local, #51).
 */
import { Fragment } from "react";
import { Info } from "lucide-react";
import { useCalendarioCaixa } from "../../hooks/useCalendarioCaixa";
import { CoberturaHint } from "../ui/CoberturaHint";
import { fmtBRL } from "../../utils/formatters";

const TOOLTIP =
  "Movimento de caixa por data de vencimento real (Zeus) no ciclo entre o 6DU " +
  "atual e o próximo. Acumulado = soma corrida (não saldo absoluto): mostra o " +
  "vale no 6DU (saída das compras) e a recuperação conforme as vendas vencem.";

function fmtDiaMes(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function fmtSigned(v: string): string {
  const n = Number(v);
  const s = fmtBRL(Math.abs(n), { decimals: 0 });
  return n < 0 ? `−${s}` : `+${s}`;
}

interface Props {
  /** #244: 6du navegado no card (YYYY-MM-DD) — ancora o ciclo exibido. Vem do
   * calendário CCEE (useProximo6du/useCalendario6dus); omitido = ciclo corrente. */
  ancoraIso?: string;
}

export function CalendarioCaixaCard({ ancoraIso }: Props = {}) {
  const { data, isLoading, error } = useCalendarioCaixa(ancoraIso);

  if (isLoading) {
    return (
      <div
        className="text-white/40 text-xs py-4 text-center"
        data-testid="calendario-caixa-loading"
      >
        Carregando calendário de caixa…
      </div>
    );
  }

  // Sem ciclo (calendário CCEE não cobre) ou sem movimento → não renderiza o
  // bloco (o card 6DU já cobre o caso). Erro idem — header global cobre o badge.
  if (error || data.ciclo_inicio === null || (data.dias.length === 0 && data.sem_data_num_trades === 0)) {
    return null;
  }

  const temSemData = data.sem_data_num_trades > 0;

  return (
    <div
      className="rounded-xl border border-white/10 bg-indra-azul-surface p-4 mt-4"
      data-testid="calendario-caixa-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            Calendário de Caixa
            <span
              className="cursor-help"
              title={TOOLTIP}
              data-testid="calendario-caixa-tooltip"
            >
              <Info size={12} className="text-white/40" />
            </span>
          </h3>
          <p className="text-white/50 text-xs mt-1" data-testid="calendario-caixa-ciclo">
            {data.ciclo_fim_estimado ? (
              // #62 (b'): ciclo fechado por fronteira ESTIMADA (proxy) —
              // somado, com o estimado sempre visível.
              <>
                Ciclo 6DU {data.ciclo_inicio ? fmtDiaMes(data.ciclo_inicio) : "—"}
                {data.ciclo_inicio_estimado ? " (estimado)" : ""} →{" "}
                {fmtDiaMes(data.ciclo_fim_estimado)} (estimado) · vencimentos reais
              </>
            ) : data.ciclo_aberto ? (
              <>
                Ciclo 6DU {data.ciclo_inicio ? fmtDiaMes(data.ciclo_inicio) : "—"}
                {data.ciclo_inicio_estimado ? " (estimado)" : ""} → sem
                próximo 6DU no calendário — ciclo aberto (exibindo até o fim do mês)
              </>
            ) : (
              <>
                Ciclo 6DU {data.ciclo_inicio ? fmtDiaMes(data.ciclo_inicio) : "—"}
                {data.ciclo_fim ? ` → ${fmtDiaMes(data.ciclo_fim)}` : " →"} · vencimentos reais
              </>
            )}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/50">
            {data.ciclo_fim_estimado
              ? "Movimento no ciclo · fronteira estimada"
              : data.ciclo_aberto
                ? "Movimento até o fim do mês"
                : "Movimento no ciclo"}
          </div>
          <div
            className={
              "text-lg font-black font-sans " +
              (Number(data.movimento_total) >= 0 ? "text-indra-positivo" : "text-indra-negativo")
            }
            data-testid="calendario-caixa-total"
          >
            {fmtSigned(data.movimento_total)}
          </div>
          {data.ciclo_aberto && !data.ciclo_fim_estimado && Number(data.alem_horizonte_net ?? 0) !== 0 && (
            <div
              className="text-[10px] text-white/40 mt-0.5"
              data-testid="calendario-caixa-alem-horizonte"
            >
              além do horizonte (não somado): {fmtSigned(data.alem_horizonte_net ?? "0")}
            </div>
          )}
        </div>
      </div>

      {/* #62 (b'): o enquadramento — a estimativa está na PARTIÇÃO, não no
          dinheiro. A banda diz o que muda de ciclo se a data real for outra. */}
      {(data.fronteiras_estimadas?.length ?? 0) > 0 && (
        <div
          className="mb-3 rounded-lg border border-amber-300/20 bg-amber-400/5 px-3 py-2"
          data-testid="calendario-caixa-banda-fronteira"
        >
          <div className="text-[10px] text-amber-200/80">
            Os valores são trades reais com data real — o estimado é ONDE o ciclo
            corta (6º dia útil, feriados considerados; calendário CCEE ainda não
            publicado).
          </div>
          {data.fronteiras_estimadas!.map((f) =>
            Number(f.no_dia_net) !== 0 || Number(f.vespera_net) !== 0 ? (
              <div
                key={`${f.lado}-${f.data}`}
                className="text-[10px] text-amber-200/60 mt-0.5"
                data-testid="calendario-caixa-banda-item"
              >
                {fmtSigned(f.no_dia_net)} vencem no próprio 6DU estimado (
                {fmtDiaMes(f.data)}) e mudam de ciclo se a data real for outra
                {Number(f.vespera_net) !== 0
                  ? `; ${fmtSigned(f.vespera_net)} na véspera-útil idem`
                  : ""}
              </div>
            ) : null,
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-white/5">
        <table className="w-full text-xs">
          <thead className="bg-indra-azul-deep/60 text-white/60 uppercase tracking-wider">
            <tr>
              <th className="px-2 py-1.5 text-left">Data</th>
              <th className="px-2 py-1.5 text-right">Entra</th>
              <th className="px-2 py-1.5 text-right">Sai</th>
              <th className="px-2 py-1.5 text-right">Net</th>
              <th className="px-2 py-1.5 text-right">Acumulado</th>
            </tr>
          </thead>
          <tbody>
            {data.dias.map((dia) => {
              const acum = Number(dia.movimento_acumulado);
              const net = Number(dia.net);
              const posAberta = Number(dia.posicao_aberta_mtm);
              return (
                <Fragment key={dia.data}>
                  <tr
                    data-testid={dia.eh_6du_atual ? "calendario-caixa-row-6du" : "calendario-caixa-row"}
                    className={
                      "border-t border-white/5 " +
                      (dia.eh_6du_atual ? "bg-indra-turquesa/10" : "hover:bg-white/5")
                    }
                  >
                    <td className="px-2 py-1.5 text-white/90">
                      {fmtDiaMes(dia.data)}
                      {dia.eh_6du_atual && (
                        <span
                          className="ml-1.5 text-[9px] uppercase tracking-wider text-indra-turquesa"
                          data-testid="calendario-caixa-label-6du"
                        >
                          {data.ciclo_inicio_estimado ? "6DU estimado · Liquidação" : "6DU · Liquidação"}
                        </span>
                      )}
                      {/* #244: "HOJE" só quando a data é hoje DE VERDADE — não
                          se confunde com a âncora navegada (6du do card). */}
                      {dia.eh_hoje && (
                        <span
                          className="ml-1.5 text-[9px] uppercase tracking-wider text-amber-300"
                          data-testid="calendario-caixa-label-hoje"
                        >
                          Hoje
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-indra-positivo">
                      {Number(dia.entra) !== 0 ? fmtBRL(Number(dia.entra), { decimals: 0 }) : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-indra-negativo">
                      {Number(dia.sai) !== 0 ? fmtBRL(Number(dia.sai), { decimals: 0 }) : "—"}
                    </td>
                    <td
                      className={
                        "px-2 py-1.5 text-right font-mono " +
                        (net >= 0 ? "text-indra-positivo" : "text-indra-negativo")
                      }
                    >
                      {fmtSigned(dia.net)}
                    </td>
                    <td
                      className={
                        "px-2 py-1.5 text-right font-mono font-semibold " +
                        (acum >= 0 ? "text-white" : "text-indra-negativo")
                      }
                      data-testid="calendario-caixa-acumulado"
                    >
                      {fmtSigned(dia.movimento_acumulado)}
                    </td>
                  </tr>
                  {/* #49 reconciliação: componente transparente da posição aberta
                      CCEE na linha do 6DU (já somado no net/acumulado acima). */}
                  {posAberta !== 0 && (
                    <tr
                      className="bg-indra-turquesa/5"
                      data-testid="calendario-caixa-posicao-aberta-anotacao"
                    >
                      <td
                        className="px-2 py-1 pl-4 text-[10px] text-white/50 italic"
                        colSpan={4}
                      >
                        ↳ inclui posição aberta CCEE (liquida no 6DU)
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-[10px] text-white/50">
                        {fmtSigned(dia.posicao_aberta_mtm)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {temSemData && (
              <tr
                className="border-t border-amber-300/20 bg-amber-400/5"
                data-testid="calendario-caixa-sem-data"
              >
                <td className="px-2 py-1.5 text-amber-200/80" title="Trades vigentes do fornecimento deste ciclo SEM data de vencimento no Zeus — fora do acumulado até resolver.">
                  sem data ({data.sem_data_num_trades})
                </td>
                <td className="px-2 py-1.5" />
                <td className="px-2 py-1.5" />
                <td className="px-2 py-1.5 text-right font-mono text-amber-200/80">
                  {fmtSigned(data.sem_data_net)}
                </td>
                <td className="px-2 py-1.5 text-right text-white/30">n/a</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* #84: universo declarado pela própria rota (decisão em destaque). */}
      <CoberturaHint cobertura={data.cobertura} sujeito="deste calendário" />
    </div>
  );
}
